import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  HelpCircle, 
  FileText, 
  Users, 
  Settings, 
  CreditCard,
  ChevronDown,
  ChevronUp,
  BookOpen,
  Lightbulb,
  Mail,
  Receipt,
  Calculator,
  ShoppingBag
} from 'lucide-react';

const FAQPage = () => {
  const [openSection, setOpenSection] = useState(null);

  const toggleSection = (index) => {
    setOpenSection(openSection === index ? null : index);
  };

  const faqSections = [
    {
      category: "Démarrage",
      icon: BookOpen,
      questions: [
        {
          q: "Comment créer ma première facture ?",
          a: "1. Cliquez sur 'Nouvelle facture' dans le menu\n2. Sélectionnez un client (ou créez-en un nouveau)\n3. Ajoutez vos articles avec prix et quantités\n4. Configurez la TVA si nécessaire\n5. Cliquez sur 'Créer la facture'\n\nVotre facture sera automatiquement numérotée (BNX-2026-0001)."
        },
        {
          q: "Comment créer un devis ?",
          a: "Le processus est similaire à une facture :\n1. Cliquez sur 'Nouveau devis'\n2. Remplissez les informations client et articles\n3. Définissez une date de validité\n4. Créez le devis\n\nUne fois accepté, vous pouvez le convertir en facture en un clic."
        },
        {
          q: "Comment ajouter mon logo ?",
          a: "1. Allez dans 'Paramètres'\n2. Dans la section 'Informations entreprise', cliquez sur 'Télécharger'\n3. Sélectionnez votre logo (PNG ou JPG, max 2MB)\n4. Enregistrez les paramètres\n\nVotre logo apparaîtra sur tous vos documents PDF."
        }
      ]
    },
    {
      category: "Clients",
      icon: Users,
      questions: [
        {
          q: "Comment gérer mes clients ?",
          a: "Dans la section 'Clients', vous pouvez :\n- Ajouter de nouveaux clients avec toutes leurs coordonnées\n- Voir l'historique des factures par client\n- Consulter le montant total facturé/payé/en attente\n- Modifier ou supprimer des clients existants"
        },
        {
          q: "Puis-je ajouter le numéro TVA de mes clients ?",
          a: "Oui ! Lors de la création ou modification d'un client, vous pouvez ajouter son numéro TVA dans le champ dédié. Il apparaîtra automatiquement sur les factures."
        }
      ]
    },
    {
      category: "TVA Suisse",
      icon: CreditCard,
      questions: [
        {
          q: "Comment configurer la TVA ?",
          a: "Dans 'Paramètres' > 'TVA & Devise' :\n- Activez/désactivez l'assujettissement à la TVA\n- Définissez le taux par défaut (7.7% en Suisse)\n- Choisissez votre devise (CHF ou EUR)\n\nSi vous n'êtes pas assujetti, la mention 'Non assujetti à la TVA' apparaîtra automatiquement."
        },
        {
          q: "Puis-je créer une facture sans TVA ?",
          a: "Oui ! Lors de la création d'une facture ou devis, vous pouvez désactiver la TVA avec le switch 'Avec TVA'. Cela permet de gérer des cas spécifiques indépendamment de vos paramètres globaux."
        },
        {
          q: "Quels taux de TVA sont utilisés en Suisse ?",
          a: "Les taux de TVA suisses sont :\n- 8.1% : Taux normal (par défaut)\n- 3.8% : Taux spécial (hébergement)\n- 2.6% : Taux réduit (alimentation, livres, médicaments)\n\nVous pouvez personnaliser le taux pour chaque article."
        }
      ]
    },
    {
      category: "Documents PDF",
      icon: FileText,
      questions: [
        {
          q: "Comment personnaliser mes factures ?",
          a: "Dans 'Paramètres' > 'Modèle de facture', vous avez 20 designs disponibles :\n- Modernes (5 couleurs)\n- Classiques (5 styles)\n- Minimalistes (3 options)\n- Créatifs avec dégradés (3)\n- Style Suisse (2)\n- Industrie (2)\n\nSélectionnez celui qui correspond à votre image."
        },
        {
          q: "Comment envoyer une facture par email ?",
          a: "Deux options disponibles :\n\n1. AVEC configuration SMTP (recommandé) :\n   - Configurez votre email dans Paramètres > Email\n   - Cliquez sur 'Envoyer par email' dans le menu de la facture\n   - Le PDF est automatiquement attaché et envoyé depuis VOTRE adresse\n\n2. SANS configuration SMTP :\n   - Cliquez sur 'Envoyer par email'\n   - Votre messagerie s'ouvre avec un message pré-rempli\n   - Attachez manuellement le PDF téléchargé"
        },
        {
          q: "Comment imprimer une facture ?",
          a: "1. Cliquez sur le menu d'actions (⋮)\n2. Sélectionnez 'Imprimer'\n3. Le PDF s'ouvrira dans une nouvelle fenêtre avec la boîte de dialogue d'impression"
        }
      ]
    },
    {
      category: "Configuration Email",
      icon: Mail,
      questions: [
        {
          q: "Comment configurer l'envoi d'emails ?",
          a: "Dans 'Paramètres' > 'Configuration Email (SMTP)', renseignez :\n- Serveur SMTP de votre fournisseur\n- Port (587 pour TLS, 465 pour SSL)\n- Votre email et mot de passe\n- Testez avec le bouton 'Tester la configuration'"
        },
        {
          q: "Comment configurer Gmail ?",
          a: "Pour Gmail, vous devez utiliser un 'Mot de passe d'application' :\n\n1. Activez la validation en 2 étapes sur votre compte Google\n2. Allez dans Sécurité > Mots de passe des applications\n3. Créez un nouveau mot de passe pour 'Messagerie'\n4. Utilisez ce mot de passe dans Xyron Core ERP\n\nConfiguration :\n- Serveur: smtp.gmail.com\n- Port: 587 (TLS)\n- Email: votre-email@gmail.com\n- Mot de passe: le mot de passe d'application généré"
        },
        {
          q: "Comment configurer Infomaniak ?",
          a: "Configuration pour Infomaniak (populaire en Suisse) :\n\n- Serveur: mail.infomaniak.com\n- Port: 587 (TLS) ou 465 (SSL)\n- Email: votre-email@votre-domaine.ch\n- Mot de passe: votre mot de passe email Infomaniak\n\nVous pouvez aussi utiliser un sous-compte email dédié à la facturation."
        },
        {
          q: "Comment configurer un autre fournisseur ?",
          a: "Consultez la documentation de votre fournisseur pour les paramètres SMTP.\n\nFournisseurs courants en Suisse :\n- Hostpoint: mail.hostpoint.ch (port 587)\n- Swisscom: smtp.bluewin.ch (port 587)\n- Green.ch: smtp.green.ch (port 587)\n\nPour les services professionnels comme Resend ou SendGrid, utilisez leurs clés API comme mot de passe."
        }
      ]
    },
    {
      category: "Intégration Shopify",
      icon: ShoppingBag,
      questions: [
        {
          q: "Comment connecter ma boutique Shopify ?",
          a: "Pour connecter Shopify, vous devez créer une App privée :\n\n1. Connectez-vous à votre admin Shopify\n2. Allez dans Paramètres > Apps et canaux de vente\n3. Cliquez sur 'Développer des apps'\n4. Cliquez sur 'Créer une app'\n5. Donnez un nom (ex: 'Xyron Core ERP')\n6. Dans 'Configuration', accordez les permissions :\n   - read_orders (lecture des commandes)\n   - read_customers (lecture des clients)\n7. Cliquez sur 'Installer l'app'\n8. Copiez le 'Admin API access token'\n9. Collez-le dans Xyron Core ERP > Shopify > Connecter"
        },
        {
          q: "Quelles permissions Shopify sont nécessaires ?",
          a: "Xyron Core ERP a besoin de 2 permissions en lecture seule :\n\n• read_orders : pour voir vos commandes\n• read_customers : pour récupérer les infos clients\n\n⚠️ Nous n'écrivons jamais dans votre Shopify. Vos données restent intactes."
        },
        {
          q: "Comment créer une facture depuis une commande Shopify ?",
          a: "Une fois connecté :\n\n1. Allez dans l'onglet 'Shopify'\n2. Vous voyez toutes vos commandes récentes\n3. Cliquez sur 'Facturer' sur une commande payée\n4. La facture est créée automatiquement avec :\n   - Les articles de la commande\n   - Les informations client (ou création d'un nouveau client)\n   - Le bon montant\n\nLa commande est ensuite marquée comme 'Facturée' pour éviter les doublons."
        },
        {
          q: "Puis-je facturer automatiquement toutes les commandes ?",
          a: "Pour l'instant, la création de factures est manuelle (en 1 clic).\n\nCela vous permet de :\n• Vérifier les commandes avant facturation\n• Gérer les cas particuliers (remboursements, etc.)\n• Garder le contrôle total\n\n💡 Astuce : Utilisez le filtre 'Payées' pour voir uniquement les commandes à facturer."
        }
      ]
    },
    {
      category: "Paramètres",
      icon: Settings,
      questions: [
        {
          q: "Quelles informations dois-je remplir ?",
          a: "Pour une facturation professionnelle en Suisse :\n- Nom de l'entreprise\n- Adresse complète\n- Numéro du Registre du Commerce (CHE-XXX.XXX.XXX)\n- Numéro TVA (si assujetti)\n- IBAN pour les paiements\n- Conditions de paiement (ex: 30 jours)"
        },
        {
          q: "Comment changer les conditions de paiement ?",
          a: "Dans 'Paramètres' > 'Textes par défaut', vous pouvez personnaliser :\n- Les conditions de paiement (délai, pénalités)\n- Les mentions légales\n\nCes textes apparaîtront sur tous vos documents."
        }
      ]
    },
    {
      category: "Dépenses & Bilan Fiscal",
      icon: Receipt,
      questions: [
        {
          q: "À quoi sert le module Dépenses ?",
          a: "Le module Dépenses vous permet de :\n- Enregistrer toutes vos dépenses professionnelles\n- Les classer par catégorie (12 catégories disponibles)\n- Calculer automatiquement votre bénéfice net\n- Préparer votre déclaration d'impôts\n\nToutes ces informations sont reprises dans votre Bilan Fiscal annuel."
        },
        {
          q: "Comment utiliser le Bilan Fiscal ?",
          a: "Le Bilan Fiscal vous offre :\n- Un récapitulatif annuel (CA, Dépenses, Bénéfice Net)\n- Des graphiques d'évolution\n- Le détail de la TVA collectée par taux\n- L'export PDF pour votre comptable\n- L'export CSV pour analyse détaillée\n\n⚠️ Ces données sont indicatives. Consultez votre fiduciaire pour validation officielle."
        },
        {
          q: "Quelles dépenses puis-je déduire ?",
          a: "En tant qu'indépendant, vous pouvez déduire les dépenses nécessaires à votre activité professionnelle. Consultez les catégories ci-dessous pour des exemples détaillés.\n\n💡 Conseil : Gardez TOUJOURS vos justificatifs (factures, reçus, tickets) pendant 10 ans minimum."
        }
      ]
    },
    {
      category: "Dépenses déductibles par catégorie",
      icon: Calculator,
      questions: [
        {
          q: "💻 Logiciels & Abonnements - Exemples déductibles",
          a: "✅ DÉDUCTIBLES À 100% :\n• Licences logiciels professionnels (Adobe, Microsoft 365, Figma)\n• Abonnements cloud (Google Workspace, Dropbox Business)\n• Outils de facturation et comptabilité\n• Antivirus et sécurité informatique\n• Abonnement site web professionnel (Hostpoint, Infomaniak)\n• Logiciels de gestion de projet (Notion, Trello Pro)\n• VPN professionnel\n• Stockage cloud (iCloud, OneDrive Business)\n\n⚠️ ATTENTION :\n• Netflix, Spotify personnel = NON déductible\n• Jeux vidéo = NON déductible\n• Usage mixte = déduire uniquement la part professionnelle"
        },
        {
          q: "🛠️ Matériel & Équipement - Exemples déductibles",
          a: "✅ DÉDUCTIBLES À 100% :\n• Ordinateur portable / PC de bureau\n• Écrans, clavier, souris, webcam\n• Imprimante et cartouches d'encre\n• Téléphone professionnel\n• Tablette pour le travail\n• Disque dur externe, clés USB\n• Caméra / appareil photo (si usage pro)\n• Micro, casque pour visioconférence\n• Mobilier de bureau (chaise, bureau)\n\n📝 AMORTISSEMENT :\n• Matériel > 1'000 CHF : amortir sur 3-5 ans\n• Matériel < 1'000 CHF : déductible immédiatement\n\n⚠️ Usage mixte (ex: ordinateur 50% pro / 50% perso) = déduire 50%"
        },
        {
          q: "🚗 Déplacements & Transport - Exemples déductibles",
          a: "✅ DÉDUCTIBLES :\n• Billets de train/bus pour rendez-vous clients\n• Abonnement CFF demi-tarif ou AG (part professionnelle)\n• Billets d'avion pour voyages d'affaires\n• Taxi/Uber pour déplacements professionnels\n• Frais de parking lors de RDV clients\n• Location de voiture (déplacements pro)\n\n🚗 VÉHICULE PRIVÉ :\n• 0.70 CHF/km (forfait fiscal suisse)\n• OU frais réels (essence, entretien, assurance)\n• Tenir un carnet de bord des trajets pros\n\n⚠️ NON DÉDUCTIBLE :\n• Trajet domicile-travail (sauf cas particuliers)\n• Vacances personnelles"
        },
        {
          q: "🍽️ Repas & Restauration - Exemples déductibles",
          a: "✅ DÉDUCTIBLES :\n• Repas d'affaires avec clients (100%)\n• Déjeuner lors de déplacements professionnels\n• Repas lors de formations/conférences\n• Invitation de partenaires commerciaux\n\n📝 CONDITIONS :\n• Garder le reçu avec nom du client/motif\n• Montant raisonnable (pas de restaurants 5 étoiles quotidiens)\n• Usage professionnel justifiable\n\n⚠️ NON DÉDUCTIBLE :\n• Repas quotidiens personnels\n• Courses alimentaires personnelles\n• Cafés/snacks sans contexte professionnel"
        },
        {
          q: "📎 Fournitures de bureau - Exemples déductibles",
          a: "✅ DÉDUCTIBLES À 100% :\n• Papier, stylos, classeurs, post-it\n• Enveloppes et timbres\n• Cartouches d'encre, toner\n• Agrafeuse, perforatrice, ciseaux\n• Calendrier, agenda professionnel\n• Étiquettes, pochettes plastiques\n• Cartes de visite\n• Tampons d'entreprise\n\n💡 Conseil : Achetez en gros pour économiser"
        },
        {
          q: "📢 Marketing & Publicité - Exemples déductibles",
          a: "✅ DÉDUCTIBLES À 100% :\n• Création de logo et charte graphique\n• Site web (création et maintenance)\n• Publicité Google Ads, Facebook Ads\n• Impression flyers, brochures, affiches\n• Frais de salon/exposition\n• Objets promotionnels (stylos, goodies)\n• Photographe professionnel\n• Vidéos promotionnelles\n• Référencement SEO\n• Email marketing (Mailchimp, etc.)\n\n📝 Gardez les factures et preuves de diffusion"
        },
        {
          q: "🎓 Formation & Conférences - Exemples déductibles",
          a: "✅ DÉDUCTIBLES :\n• Cours de perfectionnement professionnel\n• Certifications métier\n• Conférences et séminaires\n• Livres et manuels professionnels\n• Abonnements revues spécialisées\n• E-learning (Udemy, LinkedIn Learning) si lié au métier\n• Coaching professionnel\n• Frais d'inscription à des associations professionnelles\n\n⚠️ CONDITIONS :\n• Lien direct avec l'activité professionnelle\n• Formation diplômante = parfois déductible différemment"
        },
        {
          q: "🛡️ Assurances - Exemples déductibles",
          a: "✅ DÉDUCTIBLES :\n• Assurance responsabilité civile professionnelle (RC Pro)\n• Assurance perte de gain maladie\n• Assurance accidents professionnels (LAA)\n• Assurance protection juridique professionnelle\n• Cotisations AVS/AI/APG (part employeur)\n• 2ème pilier (LPP) - cotisations\n• 3ème pilier A (plafonné)\n\n⚠️ PARTIELLEMENT DÉDUCTIBLE :\n• Assurance maladie = déduction forfaitaire\n• Assurance vie = selon les cas"
        },
        {
          q: "🏢 Loyer & Charges - Exemples déductibles",
          a: "✅ DÉDUCTIBLES :\n• Loyer d'un bureau/local commercial (100%)\n• Charges (électricité, eau, chauffage du local)\n• Abonnement coworking\n• Location salle de réunion\n\n🏠 BUREAU À DOMICILE :\n• Surface dédiée exclusivement au travail\n• Calculer au prorata : (surface bureau / surface totale) × loyer\n• Exemple : 15m² bureau / 80m² appart = 18.75% du loyer\n• Applicable aussi aux charges (électricité, internet, chauffage)\n\n📝 Tenir un plan avec les surfaces"
        },
        {
          q: "📱 Téléphone & Internet - Exemples déductibles",
          a: "✅ DÉDUCTIBLES :\n• Abonnement téléphone professionnel (100%)\n• Abonnement internet au bureau (100%)\n• Forfait mobile pro\n• Ligne fixe professionnelle\n\n📱 USAGE MIXTE (téléphone perso/pro) :\n• Estimer la part professionnelle (ex: 50%)\n• Déduire uniquement cette part\n• Ou prendre un 2ème abonnement 100% pro\n\n💡 Conseil : Un forfait pro séparé simplifie la comptabilité"
        },
        {
          q: "🏦 Frais bancaires - Exemples déductibles",
          a: "✅ DÉDUCTIBLES À 100% :\n• Frais de tenue de compte professionnel\n• Commissions sur transactions\n• Frais de carte bancaire professionnelle\n• Intérêts sur prêts professionnels\n• Frais de change pour transactions internationales\n• Terminal de paiement (SumUp, etc.)\n• Frais Stripe, PayPal professionnels\n\n⚠️ NON DÉDUCTIBLE :\n• Frais de compte bancaire personnel\n• Agios sur découvert personnel"
        },
        {
          q: "📦 Autres dépenses - Exemples déductibles",
          a: "✅ DÉDUCTIBLES :\n• Frais de port et d'expédition\n• Emballages et matériel d'envoi\n• Nettoyage du local professionnel\n• Réparations matériel professionnel\n• Cadeaux clients (montant raisonnable)\n• Cotisations syndicales/associations pro\n• Frais de comptable/fiduciaire\n• Frais juridiques professionnels\n\n❌ NON DÉDUCTIBLES :\n• Amendes et contraventions\n• Vêtements non spécifiques au métier\n• Dépenses personnelles\n• Cadeaux personnels"
        }
      ]
    }
  ];

  const quickTips = [
    "Utilisez le catalogue Produits/Services pour gagner du temps lors de la création de factures",
    "Convertissez un devis en facture en un clic une fois accepté",
    "Exportez vos factures en CSV pour votre comptabilité",
    "Dupliquez une facture existante pour gagner du temps",
    "Le tableau de bord affiche vos factures en retard en rouge",
    "Enregistrez vos dépenses au fur et à mesure pour un bilan fiscal toujours à jour",
    "Gardez vos justificatifs (reçus, factures) pendant 10 ans minimum",
    "Consultez le Bilan Fiscal pour voir votre bénéfice net en temps réel"
  ];

  return (
    <div className="space-y-6 max-w-4xl" data-testid="faq-page">
      <div>
        <h1 className="font-heading text-2xl lg:text-3xl font-bold text-slate-900">Aide & FAQ</h1>
        <p className="text-slate-500 mt-1">Guide d'utilisation et questions fréquentes</p>
      </div>

      {/* Quick Tips */}
      <Card className="border-blue-200 bg-blue-50">
        <CardHeader>
          <div className="flex items-center gap-3">
            <Lightbulb className="w-5 h-5 text-blue-600" />
            <CardTitle className="font-heading text-lg text-blue-900">Astuces rapides</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2">
            {quickTips.map((tip, index) => (
              <li key={index} className="flex items-start gap-2 text-sm text-blue-800">
                <span className="text-blue-500 mt-1">•</span>
                {tip}
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      {/* FAQ Sections */}
      <div className="space-y-4">
        {faqSections.map((section, sectionIndex) => (
          <Card key={sectionIndex} className="border-slate-200">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                  <section.icon className="w-5 h-5 text-slate-600" />
                </div>
                <CardTitle className="font-heading text-lg">{section.category}</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="space-y-2">
                {section.questions.map((item, qIndex) => {
                  const index = `${sectionIndex}-${qIndex}`;
                  const isOpen = openSection === index;
                  return (
                    <div key={qIndex} className="border border-slate-200 rounded-lg overflow-hidden">
                      <button
                        onClick={() => toggleSection(index)}
                        className="w-full flex items-center justify-between p-4 text-left hover:bg-slate-50 transition-colors"
                      >
                        <span className="font-medium text-slate-900 flex items-center gap-2">
                          <HelpCircle className="w-4 h-4 text-blue-500" />
                          {item.q}
                        </span>
                        {isOpen ? (
                          <ChevronUp className="w-5 h-5 text-slate-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-slate-400" />
                        )}
                      </button>
                      {isOpen && (
                        <div className="px-4 pb-4 pt-0">
                          <div className="pl-6 text-sm text-slate-600 whitespace-pre-line border-l-2 border-blue-200 ml-2">
                            {item.a}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Contact Support */}
      <Card className="border-slate-200">
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
              <Mail className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h3 className="font-medium text-slate-900">Besoin d'aide supplémentaire ?</h3>
              <p className="text-sm text-slate-500">
                Contactez notre support via le systeme de tickets interne de l'application (menu Support).
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default FAQPage;
